import React from "react";
import { T } from "./globals";

const UI_FONT_SCALE_OPTIONS = [85, 90, 95, 100, 105, 110];
const SHORTCUT_MODULE_CHOICES = ["services","purchases","occurrences","inspections","animals","aviculture","machinery","vet","haras","services_external"];
const DEFAULT_SHORTCUT_MODULES = ["services","purchases","occurrences","inspections"];
const DEFAULT_UI_PREFERENCES = { fontScale: 100, themeMode: "dark", shortcutModules: DEFAULT_SHORTCUT_MODULES };
const LIGHT_THEME = {
  bg: "#F8FAFC", card: "#FFFFFF", card2: "#F1F5F9", border: "#CBD5E1",
  text: "#0F172A", muted: "#64748B", accent: "#2563EB", green: "#059669",
  red: "#DC2626", yellow: "#D97706", purple: "#7C3AED",
};
const DARK_THEME = {
  bg: "#0A0E14", card: "#111827", card2: "#1A2233", border: "#1F2D3D",
  text: "#E2E8F0", muted: "#64748B", accent: "#3B82F6", green: "#10B981",
  red: "#EF4444", yellow: "#F59E0B", purple: "#8B5CF6",
};

function normalizeUiPreferences(raw) {
  const base = raw && typeof raw === "object" ? raw : {};
  const requestedScale = Number(base?.fontScale);
  const fontScale = UI_FONT_SCALE_OPTIONS.includes(requestedScale) ? requestedScale : DEFAULT_UI_PREFERENCES.fontScale;
  const themeMode = ["auto", "light", "dark"].includes(String(base?.themeMode || "").trim().toLowerCase())
    ? String(base.themeMode).trim().toLowerCase()
    : DEFAULT_UI_PREFERENCES.themeMode;
  const shortcutModules = Array.isArray(base?.shortcutModules)
    ? [...new Set(base.shortcutModules.map(v=>String(v||"").trim()).filter(v=>SHORTCUT_MODULE_CHOICES.includes(v)))].slice(0,4)
    : [];
  return { fontScale, themeMode, shortcutModules: shortcutModules.length ? shortcutModules : [...DEFAULT_SHORTCUT_MODULES] };
}

function resolveThemeMode(uiPreferences, prefersDark) {
  const prefs = normalizeUiPreferences(uiPreferences);
  if (prefs.themeMode === "auto") return prefersDark ? "dark" : "light";
  return prefs.themeMode;
}

function applyThemeTokens(themeName) {
  const tokens = themeName === "light" ? LIGHT_THEME : DARK_THEME;
  Object.assign(T, tokens);
  if (typeof document !== "undefined") {
    const root = document.documentElement;
    root.setAttribute("data-fz-theme", themeName);
    root.style.colorScheme = themeName;
    document.body.style.background = tokens.bg;
    document.body.style.color = tokens.text;
  }
}

function getContrastTextColor(hexColor) {
  const raw = String(hexColor || "").replace("#", "").trim();
  if (!/^[0-9a-fA-F]{6}$/.test(raw)) return "#F8FAFC";
  const r = parseInt(raw.slice(0,2), 16);
  const g = parseInt(raw.slice(2,4), 16);
  const b = parseInt(raw.slice(4,6), 16);
  const luminance = (0.299*r + 0.587*g + 0.114*b) / 255;
  return luminance > 0.62 ? "#0F172A" : "#F8FAFC";
}

function ShortcutMonoIcon({ type, size=22, color="currentColor", stroke=2 }) {
  const common = { width:size, height:size, viewBox:"0 0 24 24", fill:"none", stroke:color, strokeWidth:stroke, strokeLinecap:"round", strokeLinejoin:"round", style:{display:"block"} };
  switch(String(type||"home")) {
    case "home":
      return <svg {...common}><path d="M3 10.5 12 3l9 7.5"/><path d="M5 9.5V21h14V9.5"/><path d="M9.5 21v-6h5v6"/></svg>;
    case "services":
      return <svg {...common}><path d="m14 5 5 5"/><path d="m5 19 6-1 9-9a2.1 2.1 0 0 0-3-3l-9 9-1 6Z"/><path d="M3 21h18"/></svg>;
    case "purchases":
      return <svg {...common}><circle cx="9" cy="19" r="1.5"/><circle cx="17" cy="19" r="1.5"/><path d="M3 5h2l2.2 9.2a1 1 0 0 0 1 .8h8.9a1 1 0 0 0 1-.8L20 8H7"/></svg>;
    case "occurrences":
      return <svg {...common}><path d="M12 4 3.5 19h17L12 4Z"/><path d="M12 9v4"/><circle cx="12" cy="16.5" r=".8" fill={color} stroke="none"/></svg>;
    case "inspections":
      return <svg {...common}><circle cx="11" cy="11" r="6"/><path d="m20 20-4.2-4.2"/></svg>;
    case "animals":
      return <svg {...common}><path d="M4.5 14.6c0-2.5 2-4.6 4.6-4.6h5.1c2.7 0 4.8 1.9 4.8 4.3v2.9h-2.2"/><path d="M6 14.6 4.1 11.4"/><path d="M16.7 10.4 19.7 8.9l.9 1.8"/><path d="M8 10 7 7.5"/><path d="M12.3 10.1 13.2 8.2"/><path d="M8.4 16.9V20"/><path d="M14.6 16.9V20"/><path d="M18 14.6V20"/><path d="M6.4 12.3h1.8"/><circle cx="15.5" cy="12.8" r=".7" fill={color} stroke="none"/></svg>;
    case "aviculture":
      return <svg {...common}><path d="M7.1 13.2c0-3.3 2.4-5.9 5.8-5.9 2.1 0 3.8.7 5 2"/><path d="M7.6 13.6c-1.3.7-2.1 1.9-2.1 3.4 0 1.5 1.2 2.7 2.9 2.7h5.2c2.8 0 4.9-1.7 4.9-4.1 0-2.3-1.8-4.1-4.3-4.1-.8 0-1.5.1-2.1.4"/><path d="M15.9 8.5c.2-1 .8-1.8 1.9-2.6"/><path d="M18 10.9 21 9.6"/><path d="M6.9 10.9 5 9.2"/><path d="M6.4 12.2 3.6 11.9"/><path d="M8 19.6 7.3 21"/><path d="M11.7 19.6V21"/><path d="M15 19.6l.7 1.4"/><circle cx="14.5" cy="10.5" r=".72" fill={color} stroke="none"/></svg>;
    case "services_external":
      return <svg {...common}><path d="M8.2 10.1 12 7l3.8 3.1"/><path d="M9 10V8.8c0-1.6 1.3-2.8 3-2.8s3 1.2 3 2.8V10"/><circle cx="12" cy="12.5" r="2.2"/><path d="M7.6 20v-3.2c0-2.1 1.9-3.8 4.4-3.8s4.4 1.7 4.4 3.8V20"/><path d="M10 20v-2.8"/><path d="M14 20v-2.8"/></svg>;
    case "machinery":
      return <svg {...common}><circle cx="7.5" cy="17.6" r="2.6"/><circle cx="17.4" cy="17.2" r="1.8"/><path d="M4.4 17.6v-4.3H12l2.2 2.1h4.1v2.2"/><path d="M9.8 13.3V9.4h3.6l2.2 3.9"/><path d="M5.7 11h2.5"/><path d="M15.4 9.4h2.1"/></svg>;
    case "vet":
      return <svg {...common}><circle cx="12" cy="7" r="2.4"/><path d="M7.5 20v-3.7c0-2.3 1.9-4.1 4.3-4.1h.4c2.4 0 4.3 1.8 4.3 4.1V20"/><path d="M6 13.8 3.9 16"/><path d="M18 13.8 20.1 16"/><path d="M10 20v-3.4"/><path d="M14 20v-3.4"/></svg>;
    case "haras":
      return <svg {...common}><path d="M7 14c0-3.9 3.1-7 7-7h2"/><path d="M7 14v5"/><path d="M11 14v5"/><path d="M16 14v5"/><path d="M8 9 6 6"/><path d="M18 7l2-2"/></svg>;
    default:
      return <svg {...common}><circle cx="12" cy="12" r="7"/></svg>;
  }
}


// AUTH ACCOUNTS — stored globally in localStorage as "auth:accounts"
// ═══════════════════════════════════════════════════════════════════════════════

export { UI_FONT_SCALE_OPTIONS, SHORTCUT_MODULE_CHOICES, DEFAULT_SHORTCUT_MODULES, DEFAULT_UI_PREFERENCES };
export { LIGHT_THEME, DARK_THEME };
export { normalizeUiPreferences, resolveThemeMode, applyThemeTokens, getContrastTextColor, ShortcutMonoIcon };
